import { useState, useCallback, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, generatePath } from 'react-router-dom';

import { useRoutepaths } from '@/router/hooks/useRoutePaths';

import {
  TDashboardConfigSettingsSearch,
  TDashboardConfigSettingsSearchGroup,
  DashboardSearchGroupKeys,
} from '@/store/models/dashboard-config.model';
import { useSearchMenu } from '@/hooks/search/useSearchFields';
import { ISearchFormFields, TSearchGroup, SEARCH_GROUP } from '@/hooks/search/useSearch.types';
import { extractWalletIdFromIBAN } from '@/utils/tools/IBAN/IBANTools';

const defaultValues: ISearchFormFields = {
  search: undefined,
  type: undefined,
};

function parseSearchFilter(field: TDashboardConfigSettingsSearch, value: string) {
  switch (field) {
    case 'userId':
    case 'walletId':
    case 'cardToken':
      return { [field]: Number(value) };
    case 'userName':
      return { name: value };
    case 'walletIban':
      return { walletId: extractWalletIdFromIBAN(value)?.walletId };
    case 'cardEmbossedName':
      return { embossedName: value };
    case 'beneficiaryId':
      return { id: Number(value) };
    case 'beneficiaryIban': {
      return { IbanInClear: value };
    }
    case 'beneficiaryName': {
      return { name: value };
    }
    default:
      return { [field]: value };
  }
}

export function useSearch() {
  const [isOpen, setOpen] = useState(false);

  const ref = useRef<HTMLFormElement>(null);
  const PathRoute = useRoutepaths();
  const navigate = useNavigate();

  const methods = useForm<ISearchFormFields>({ defaultValues });
  const { searchFields } = useSearchMenu({ search: methods.watch('search') });

  const searchGroups: TSearchGroup = useMemo(
    () =>
      DashboardSearchGroupKeys.reduce((acc, currentKey) => {
        const groupFields = searchFields.filter((field) => SEARCH_GROUP[currentKey].includes(field?.value));
        if (groupFields?.length > 0) {
          acc[currentKey] = groupFields;
        }
        return acc;
      }, {} as TSearchGroup),
    [searchFields],
  );

  const handleSubmit = useCallback(() => {
    const { type, search } = methods.getValues();
    const filter = parseSearchFilter(type.value, search);

    if (type.group === 'user') {
      navigate(generatePath(PathRoute.app.root, { '*': PathRoute.app.users.main }), {
        state: {
          search: {
            key: Object.keys(filter)[0],
            value: Object.values(filter)[0],
          },
          attemp: Math.random(),
        },
      });
    }

    if (type.group === 'wallet') {
      navigate(generatePath(PathRoute.app.root, { '*': PathRoute.app.wallets }), {
        state: {
          search: {
            key: Object.keys(filter)[0],
            value: Object.values(filter)[0],
          },
          typeId: null,
          attemp: Math.random(),
        },
      });
    }

    if (type.group === 'card') {
      navigate(generatePath(PathRoute.app.root, { '*': PathRoute.app.cards }), {
        state: {
          search: {
            key: Object.keys(filter)[0],
            value: Object.values(filter)[0],
          },
          attemp: Math.random(),
        },
      });
    }

    if (type.group === 'beneficiary') {
      navigate(generatePath(PathRoute.app.root, { '*': PathRoute.app.beneficiaries }), {
        state: {
          search: {
            key: Object.keys(filter)[0],
            value: Object.values(filter)[0],
          },
          type: null,
          attemp: Math.random(),
        },
      });
    }

    setOpen(false);
  }, [methods, PathRoute.app, navigate]);

  const handleItemSelected = useCallback(
    (value: TDashboardConfigSettingsSearch, group: TDashboardConfigSettingsSearchGroup) => {
      methods.setValue('type', {
        group,
        value,
      });
      handleSubmit();
    },
    [methods, handleSubmit],
  );

  return { ref, methods, isOpen, setOpen, searchGroups, handleItemSelected };
}
